import { useMutation } from '@tanstack/react-query';

import {
  invalidateAllPlanQueries,
  invalidateCertificateQueries,
} from '@/utils/queryInvalidation';

import { assignUsers } from '@/_generated/api-clients/xp';

interface AssignTeamMembersParams {
  planId: string;
  userIds: string[];
  dueDate?: string;
}

/**
 * Assigns users to a plan via the consolidated `assignUsers` endpoint.
 * The backend handles reassignment, recertification resets, and due-date updates.
 *
 * After the mutation resolves, invalidates common plan/assignment queries
 * and certificate queries. Does not show toasts — consumers handle their own.
 */
export const useAssignTeamMembers = () => {
  const mutation = useMutation({
    mutationFn: async ({
      planId,
      userIds,
      dueDate,
    }: AssignTeamMembersParams) => {
      if (userIds.length === 0) return;

      await assignUsers({
        path: { planId },
        body: { userIds, ...(dueDate !== undefined && { dueDate }) },
        throwOnError: true,
      });
    },
    onSuccess: async (_data, { planId, userIds }) => {
      await Promise.all([
        invalidateAllPlanQueries(planId),
        invalidateCertificateQueries(planId, userIds),
      ]);
    },
  });

  return {
    assignTeamMembers: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};
